import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './PasswordStrengthMeter.module.css';

const PASSWORD_RULES = [
  {
    id: 'length',
    label: 'At least 10 characters',
    test: (value) => value.length >= 10,
  },
  {
    id: 'uppercase',
    label: 'One uppercase letter',
    test: (value) => /[A-Z]/.test(value),
  },
  {
    id: 'lowercase',
    label: 'One lowercase letter',
    test: (value) => /[a-z]/.test(value),
  },
  {
    id: 'number',
    label: 'One number',
    test: (value) => /\d/.test(value),
  },
  {
    id: 'symbol',
    label: 'One symbol',
    test: (value) => /[^A-Za-z\d]/.test(value),
  },
  {
    id: 'maxLength',
    label: '128 characters or fewer',
    test: (value) => value.length > 0 && value.length <= 128,
  },
];

const strengthLabels = ['Very weak', 'Weak', 'Fair', 'Good', 'Strong'];

export function getPasswordStrength(password = '') {
  const value = String(password || '');
  const checks = PASSWORD_RULES.map((rule) => ({
    ...rule,
    met: rule.test(value),
  }));
  const metCount = checks.filter((rule) => rule.met).length;
  const score = value ? Math.min(4, Math.floor((metCount / checks.length) * 5)) : 0;
  const isValid = checks.every((rule) => rule.met);

  return {
    checks,
    score,
    isValid,
    label: isValid ? 'Strong' : strengthLabels[score],
    percent: value ? Math.max(12, ((score + 1) / 5) * 100) : 0,
  };
}

export default function PasswordStrengthMeter({ password, compact = false }) {
  const { t } = useTranslation();
  const strength = useMemo(() => getPasswordStrength(password), [password]);
  const toneClass = strength.isValid ? styles.valid : styles[`score${strength.score}`];

  return (
    <div className={`${styles.meter} ${compact ? styles.compact : ''}`} aria-live="polite">
      <div className={styles.header}>
        <span>{t('Password strength')}</span>
        <strong className={toneClass}>{t(strength.label)}</strong>
      </div>
      <div className={styles.track} aria-hidden="true">
        <div
          className={`${styles.fill} ${toneClass}`}
          style={{ width: `${strength.percent}%` }}
        />
      </div>
      <ul className={styles.rules}>
        {strength.checks.map((rule) => (
          <li key={rule.id} className={rule.met ? styles.met : styles.missing}>
            <span aria-hidden="true">{rule.met ? 'OK' : '-'}</span>
            {t(rule.label)}
          </li>
        ))}
      </ul>
    </div>
  );
}
